import {
  PricingPlan,
  Organisation,
  Project,
  UsageData,
  PricingCalculatorInputs,
  PricingCalculatorResult,
  HealthCheck,
  Feature,
  CreateFeatureRequest,
  SimpleFeature,
  CreateSimpleFeatureRequest,
  StripeSubscription,
  Application,
  ApplicationQueueStats,
  BlockedEmail,
} from '../types';
import {
  Invoice,
  CreateInvoiceRequest,
  UpdateInvoiceRequest,
  InvoiceSearchFilters,
  PaymentTermPreset,
  PaymentTermType,
} from '../shared';
import type { ServerUser, ServerProject, ServerStreamingServer } from '../types/platform';

// Feature status interface
export interface FeatureStripeStatus {
  totalFeatures: number;
  stripeSynced: number;
  missingFromStripe: FeatureSyncIssue[];
  priceIdMismatches: FeatureSyncIssue[];
  duplicateProducts: FeatureSyncIssue[];
  lastSyncedAt?: string;
}

export interface FeatureSyncIssue {
  featureId: string;
  featureName: string;
  issue: string;
  expectedValue?: string;
  actualValue?: string;
  recommendation?: string;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Custom event dispatched when the API receives a 401, signaling session expiry
export const AUTH_EXPIRED_EVENT = 'auth:session-expired';

// Type conversion utilities
function featureToSimple(feature: Feature): SimpleFeature {
  return {
    id: feature.id,
    name: feature.name,
    displayName: feature.displayName,
    description: feature.description,
    category: feature.category,
    type: feature.type,
    defaultValue: feature.type === 'boolean' ? false : 0,
    isActive: feature.isActive,
    createdAt: feature.createdAt,
    updatedAt: feature.updatedAt,
  };
}
function simpleToFeature(simpleFeature: CreateSimpleFeatureRequest): CreateFeatureRequest {
  return {
    name: simpleFeature.name,
    displayName: simpleFeature.displayName,
    description: simpleFeature.description,
    category: simpleFeature.category,
    type: simpleFeature.type,
    pricing: {
      defaultCostType: 'flat_fee',
      flatFee: {
        application: 'per_plan',
        costs: {
          daily: 0,
          monthly: 0,
          yearly: 0,
        },
        currency: 'USD',
      },
      percentageFee: {
        type: 'percentage_of_plan',
        percentage: 0,
      },
      overagePrice: 0,
    },
    config: {
      isCore: false,
      isVisible: true,
      requiresSetup: false,
      defaultQuantity:
        typeof simpleFeature.defaultValue === 'number' ? simpleFeature.defaultValue : 0,
    },
  };
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const response = await fetch(url, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      if (response.status === 401) {
        window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
      }

      let errorMessage = `API request failed: ${response.status} ${response.statusText}`;
      try {
        const errorBody = await response.text();
        if (errorBody) {
          console.error('API Error Response Body:', errorBody);
          errorMessage += ` - ${errorBody}`;
        }
      } catch (e) {
        console.error('Could not parse error response body:', e);
      }
      throw new Error(errorMessage);
    }

    return response.json();
  }

  // Health check
  async healthCheck(): Promise<HealthCheck> {
    return this.request<HealthCheck>('/health');
  }

  // Pricing plans
  async getPlans(): Promise<PricingPlan[]> {
    const response = await this.request<{ success: boolean; data: PricingPlan[] } | PricingPlan[]>(
      '/api/pricing/plans'
    );
    // Handle both direct array and API response wrapper
    if (Array.isArray(response)) {
      return response;
    } else if (response && typeof response === 'object' && 'data' in response) {
      return response.data;
    }
    return [];
  }

  // Get global plans (predefined system plans)
  async getGlobalPlans(): Promise<PricingPlan[]> {
    const response = await this.request<{ success: boolean; data: PricingPlan[] } | PricingPlan[]>(
      '/api/pricing/plans/global'
    );
    // Handle both direct array and API response wrapper
    if (Array.isArray(response)) {
      return response;
    } else if (response && typeof response === 'object' && 'data' in response) {
      return response.data;
    }
    return [];
  }

  // Get all plans (both custom and global)
  async getAllPlans(): Promise<PricingPlan[]> {
    // The /api/pricing/plans endpoint already returns both custom and global plans
    // so we don't need to combine with /api/pricing/plans/global to avoid duplicates
    return this.getPlans();
  }

  async getPlan(id: string): Promise<PricingPlan> {
    return this.request<PricingPlan>(`/api/pricing/plans/${id}`);
  }

  async createPlan(data: Partial<PricingPlan>): Promise<PricingPlan> {
    const response = await this.request<{ success: boolean; data: PricingPlan }>(
      '/api/pricing/plans',
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
    return response.data;
  }

  async updatePlan(id: string, data: Partial<PricingPlan>): Promise<PricingPlan> {
    const response = await this.request<{ success: boolean; data: PricingPlan }>(
      `/api/pricing/plans/${encodeURIComponent(id)}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );
    return response.data;
  }

  async deletePlan(planId: string): Promise<{ deleted: boolean; message: string }> {
    const response = await this.request<{
      success: boolean;
      data: { deleted: boolean; message: string };
    }>(`/api/pricing/plans/${planId}`, {
      method: 'DELETE',
    });
    return response.data;
  }

  async canDeletePlan(planId: string): Promise<{ canDelete: boolean; reason?: string }> {
    const response = await this.request<{
      success: boolean;
      data: { canDelete: boolean; reason?: string };
    }>(`/api/pricing/plans/${planId}/can-delete`);
    return response.data;
  }

  async syncPlans(): Promise<{ success: boolean; message: string }> {
    const response = await this.request<{ success: boolean; message: string }>(
      '/api/pricing/sync',
      {
        method: 'POST',
      }
    );
    return response;
  }

  async cleanupOrphanedStripeReferences(): Promise<{
    success: boolean;
    message: string;
    data: { cleaned: string[]; errors: string[] };
  }> {
    const response = await this.request<{
      success: boolean;
      message: string;
      data: { cleaned: string[]; errors: string[] };
    }>('/api/pricing/cleanup', {
      method: 'POST',
    });
    return response;
  }

  async purgeOrphanedStripeProducts(): Promise<{
    success: boolean;
    message: string;
    data: {
      purged: Array<{ productId: string; name: string; prices: string[] }>;
      errors: Array<{ productId: string; error: string }>;
      summary: string;
    };
  }> {
    const response = await this.request<{
      success: boolean;
      message: string;
      data: {
        purged: Array<{ productId: string; name: string; prices: string[] }>;
        errors: Array<{ productId: string; error: string }>;
        summary: string;
      };
    }>('/api/pricing/purge', {
      method: 'POST',
    });
    return response;
  }

  async cleanupPlansWithoutStripeIDs(): Promise<{
    success: boolean;
    message: string;
    data: {
      cleaned: string[];
      errors: string[];
      summary: string;
    };
  }> {
    const response = await this.request<{
      success: boolean;
      message: string;
      data: {
        cleaned: string[];
        errors: string[];
        summary: string;
      };
    }>('/api/pricing/cleanup-no-stripe', {
      method: 'POST',
    });
    return response;
  }

  async findDuplicatePlans(): Promise<{
    success: boolean;
    message: string;
    data: {
      duplicates: Array<{ id: string; count: number; plans: PricingPlan[] }>;
      summary: string;
    };
  }> {
    const response = await this.request<{
      success: boolean;
      message: string;
      data: {
        duplicates: Array<{ id: string; count: number; plans: PricingPlan[] }>;
        summary: string;
      };
    }>('/api/pricing/duplicates');
    return response;
  }

  async cleanupDuplicatePlans(): Promise<{
    success: boolean;
    message: string;
    data: {
      cleaned: string[];
      errors: string[];
      summary: string;
    };
  }> {
    const response = await this.request<{
      success: boolean;
      message: string;
      data: {
        cleaned: string[];
        errors: string[];
        summary: string;
      };
    }>('/api/pricing/duplicates/cleanup', {
      method: 'POST',
    });
    return response;
  }

  // Invoices
  async getInvoices(organisationId: string): Promise<Invoice[]> {
    const response = await this.request<{ success: boolean; data: Invoice[] }>(
      `/api/billing/organisations/${organisationId}/invoices`
    );
    return response.data;
  }

  async getInvoice(organisationId: string, invoiceId: string): Promise<Invoice> {
    const response = await this.request<{ success: boolean; data: Invoice }>(
      `/api/billing/organisations/${organisationId}/invoices/${invoiceId}`
    );
    return response.data;
  }

  async createInvoice(organisationId: string, invoiceData: CreateInvoiceRequest): Promise<Invoice> {
    const response = await this.request<{ success: boolean; data: Invoice }>(
      `/api/billing/organisations/${organisationId}/invoices`,
      {
        method: 'POST',
        body: JSON.stringify(invoiceData),
      }
    );
    return response.data;
  }

  async updateInvoice(
    organisationId: string,
    invoiceId: string,
    updateData: UpdateInvoiceRequest
  ): Promise<Invoice> {
    const response = await this.request<{ success: boolean; data: Invoice }>(
      `/api/billing/organisations/${organisationId}/invoices/${invoiceId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(updateData),
      }
    );
    return response.data;
  }

  async searchInvoices(organisationId: string, filters: InvoiceSearchFilters): Promise<Invoice[]> {
    const query = new URLSearchParams();
    if (filters.status) filters.status.forEach((status) => query.append('status', status));
    if (filters.dateFrom) query.append('dateFrom', filters.dateFrom.toISOString());
    if (filters.dateTo) query.append('dateTo', filters.dateTo.toISOString());
    if (filters.amountFrom !== undefined) query.append('amountFrom', filters.amountFrom.toString());
    if (filters.amountTo !== undefined) query.append('amountTo', filters.amountTo.toString());
    if (filters.currency) query.append('currency', filters.currency);
    if (filters.purchaseOrderNumber)
      query.append('purchaseOrderNumber', filters.purchaseOrderNumber);
    if (filters.customerName) query.append('customerName', filters.customerName);
    const response = await this.request<{ success: boolean; data: Invoice[] }>(
      `/api/billing/organisations/${organisationId}/invoices?${query.toString()}`
    );
    return response.data;
  }

  async checkForOrphanedStripeProducts(): Promise<{
    hasOrphans: boolean;
    count: number;
    orphanedProducts: Array<{ productId: string; name: string }>;
  }> {
    // For now, we'll simply return false to avoid unnecessary API calls
    // In the future, this could be a separate check endpoint
    return {
      hasOrphans: false,
      count: 0,
      orphanedProducts: [],
    };
  }

  async calculatePricing(inputs: PricingCalculatorInputs): Promise<PricingCalculatorResult> {
    return this.request<PricingCalculatorResult>('/api/pricing/calculate', {
      method: 'POST',
      body: JSON.stringify(inputs),
    });
  }

  // Organizations
  async getOrganisations(options?: {
    status?: 'active' | 'archived' | 'all';
  }): Promise<Organisation[]> {
    const query = options?.status ? `?status=${encodeURIComponent(options.status)}` : '';
    const response = await this.request<
      { success: boolean; data: Organisation[] } | Organisation[]
    >(`/api/organisations${query}`);
    // Handle both direct array and API response wrapper
    if (Array.isArray(response)) {
      return response;
    } else if (response && typeof response === 'object' && 'data' in response) {
      return response.data;
    }
    return [];
  }

  async getOrganisation(id: string): Promise<Organisation> {
    return this.request<Organisation>(`/api/organisations/${id}`);
  }

  async createOrganisation(data: {
    name: string;
    billingEmail: string;
    planId: string;
    enabled?: boolean;
    billingMethod?: 'automated' | 'invoice';
  }): Promise<Organisation> {
    const response = await this.request<{ success: boolean; data: Organisation } | Organisation>(
      '/api/organisations',
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
    // Handle both direct organisation and API response wrapper
    if (response && typeof response === 'object' && 'data' in response) {
      return response.data;
    }
    return response as Organisation;
  }

  async updateOrganisation(
    id: string,
    data: Partial<{
      name: string;
      billingEmail: string;
      planId: string;
      enabled: boolean;
      billingMethod: 'automated' | 'invoice';
    }>
  ): Promise<Organisation> {
    const response = await this.request<{ success: boolean; data: Organisation }>(
      `/api/organisations/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );
    return response.data;
  }

  async assignPlan(
    id: string,
    planId: string,
    billingMethod: 'automated' | 'invoice'
  ): Promise<{ organisation: Organisation; customerPortalUrl: string | null }> {
    const response = await this.request<{
      success: boolean;
      data: { organisation: Organisation; customerPortalUrl: string | null };
    }>(`/api/organisations/${id}/assign-plan`, {
      method: 'POST',
      body: JSON.stringify({ planId, billingMethod }),
    });
    return response.data;
  }

  async deleteOrganisation(id: string): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>(`/api/organisations/${id}`, {
      method: 'DELETE',
    });
  }

  async archiveOrganisation(id: string, reason?: string): Promise<Organisation> {
    const response = await this.request<{ success: boolean; data: Organisation }>(
      `/api/organisations/${id}/archive`,
      {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }
    );
    return response.data;
  }

  async restoreOrganisation(id: string): Promise<Organisation> {
    const response = await this.request<{ success: boolean; data: Organisation }>(
      `/api/organisations/${id}/restore`,
      {
        method: 'POST',
      }
    );
    return response.data;
  }

  async canDeleteOrganisation(id: string): Promise<{ canDelete: boolean; reason?: string }> {
    // For now, we'll assume all organizations can be deleted unless they have active projects
    // This could be enhanced with a backend endpoint similar to canDeletePlan
    try {
      const projects = await this.getProjects();
      const orgProjects = projects.filter((p) => p.organisationId === id);
      if (orgProjects.length > 0) {
        return {
          canDelete: false,
          reason: `Organisation has ${orgProjects.length} active project(s). Please delete or reassign projects first.`,
        };
      }
      return { canDelete: true };
    } catch {
      return { canDelete: false, reason: 'Error checking organisation dependencies' };
    }
  }

  /**
   * Start an async Stripe sync job. Returns immediately with the
   * jobId; poll getStripeSyncJob(jobId) for progress.
   */
  async startStripeSyncJob(): Promise<{
    success: boolean;
    data: { jobId: string; status: 'queued' };
  }> {
    return this.request<{
      success: boolean;
      data: { jobId: string; status: 'queued' };
    }>('/api/organisations/sync', { method: 'POST' });
  }

  async getStripeSyncJob(jobId: string): Promise<{
    success: boolean;
    data: {
      jobId: string;
      status: 'queued' | 'running' | 'completed' | 'failed';
      total: number;
      processed: number;
      created: string[];
      removed: string[];
      verified: string[];
      errors: string[];
      failureReason: string | null;
      startedAt: string;
      finishedAt: string | null;
    };
  }> {
    return this.request(`/api/organisations/sync/${encodeURIComponent(jobId)}`);
  }

  // Projects
  async getProjects(): Promise<Project[]> {
    const response = await this.request<{ success: boolean; data: Project[] } | Project[]>(
      '/api/projects'
    );
    // Handle both direct array and API response wrapper
    if (Array.isArray(response)) {
      return response;
    } else if (response && typeof response === 'object' && 'data' in response) {
      return response.data || [];
    }
    return [];
  }

  async getProject(id: string): Promise<Project> {
    return this.request<Project>(`/api/projects/${id}`);
  }

  async createProject(data: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>): Promise<Project> {
    return this.request<Project>('/api/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Usage data
  async getUsage(organisationId?: string, projectId?: string): Promise<UsageData[]> {
    const params = new URLSearchParams();
    if (organisationId) params.append('organisationId', organisationId);
    if (projectId) params.append('projectId', projectId);

    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<UsageData[]>(`/api/usage${query}`);
  }

  // Subscription operations
  async getSubscription(
    organisationId: string
  ): Promise<{ success: boolean; data: StripeSubscription }> {
    const response = await this.request<{ success: boolean; data: StripeSubscription }>(
      `/api/billing/organizations/${organisationId}/subscription`
    );
    return response;
  }

  async cancelSubscription(
    organisationId: string,
    cancelAtPeriodEnd: boolean = true
  ): Promise<{ success: boolean; message: string; data: StripeSubscription }> {
    return this.request<{ success: boolean; message: string; data: StripeSubscription }>(
      `/api/billing/organizations/${organisationId}/subscription/cancel`,
      {
        method: 'POST',
        body: JSON.stringify({ cancelAtPeriodEnd }),
      }
    );
  }

  async reactivateSubscription(
    organisationId: string
  ): Promise<{ success: boolean; message: string; data: StripeSubscription }> {
    return this.request<{ success: boolean; message: string; data: StripeSubscription }>(
      `/api/billing/organizations/${organisationId}/subscription/reactivate`,
      {
        method: 'POST',
      }
    );
  }

  // Billing operations
  async upgradePlan(
    organisationId: string,
    planId: string
  ): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>(`/api/billing/upgrade`, {
      method: 'POST',
      body: JSON.stringify({ organisationId, planId }),
    });
  }

  async downgradePlan(
    organisationId: string,
    planId: string
  ): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>(`/api/billing/downgrade`, {
      method: 'POST',
      body: JSON.stringify({ organisationId, planId }),
    });
  }

  async validateLimits(
    organisationId: string,
    projectId: string
  ): Promise<{ valid: boolean; violations: string[] }> {
    return this.request<{ valid: boolean; violations: string[] }>(`/api/billing/validate-limits`, {
      method: 'POST',
      body: JSON.stringify({ organisationId, projectId }),
    });
  }

  // Features
  async getFeatures(): Promise<Feature[]> {
    const response = await this.request<{ success: boolean; data: Feature[] } | Feature[]>(
      '/api/features'
    );
    // Handle both direct array and API response wrapper
    if (Array.isArray(response)) {
      return response;
    } else if (response && typeof response === 'object' && 'data' in response) {
      return response.data || [];
    }
    return [];
  }

  async getFeature(id: string): Promise<Feature> {
    return this.request<Feature>(`/api/features/${id}`);
  }

  async createFeature(data: CreateFeatureRequest): Promise<Feature> {
    const response = await this.request<{ success: boolean; data: Feature } | Feature>(
      '/api/features',
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
    // Handle both direct feature and API response wrapper
    if (response && typeof response === 'object' && 'data' in response) {
      return response.data;
    }
    return response as Feature;
  }

  async updateFeature(
    id: string,
    data: Partial<CreateFeatureRequest> & { isActive?: boolean }
  ): Promise<Feature> {
    const response = await this.request<{ success: boolean; data: Feature } | Feature>(
      `/api/features/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );
    // Handle both direct feature and API response wrapper
    if (response && typeof response === 'object' && 'data' in response) {
      return response.data;
    }
    return response as Feature;
  }

  async deleteFeature(id: string): Promise<void> {
    return this.request<void>(`/api/features/${id}`, {
      method: 'DELETE',
    });
  }

  // Simplified feature methods for frontend
  async getSimpleFeatures(): Promise<SimpleFeature[]> {
    const features = await this.getFeatures();
    return features.map(featureToSimple);
  }

  async createSimpleFeature(data: CreateSimpleFeatureRequest): Promise<SimpleFeature> {
    const feature = await this.createFeature(simpleToFeature(data));
    return featureToSimple(feature);
  }

  async updateSimpleFeature(
    id: string,
    data: Partial<CreateSimpleFeatureRequest>
  ): Promise<SimpleFeature> {
    const updateData: Partial<CreateFeatureRequest> & { isActive?: boolean } = {};

    if (data.name) updateData.name = data.name;
    if (data.displayName) updateData.displayName = data.displayName;
    if (data.description) updateData.description = data.description;
    if (data.category) updateData.category = data.category;
    if (data.type) updateData.type = data.type;
    if (data.isActive !== undefined) updateData.isActive = data.isActive;

    const feature = await this.updateFeature(id, updateData);
    return featureToSimple(feature);
  }

  // Audit Log methods
  async getAuditLogs(
    params: {
      page?: number;
      limit?: number;
      action?: string;
      objectType?: string;
      userId?: string;
      userEmail?: string;
      dateFrom?: string;
      dateTo?: string;
      search?: string;
    } = {}
  ): Promise<{
    logs: Array<{
      id: string;
      title: string;
      action: 'CREATE' | 'UPDATE' | 'DELETE' | 'SYNC' | 'CLEANUP';
      objectType: string;
      objectId: string;
      objectName?: string;
      changes?: Record<string, unknown>;
      userId?: string;
      userEmail?: string;
      ipAddress?: string;
      userAgent?: string;
      createdAt: string;
    }>;
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    const queryParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });

    const queryString = queryParams.toString();
    const endpoint = `/api/audit/logs${queryString ? `?${queryString}` : ''}`;

    const response = await this.request<{
      success: boolean;
      data: {
        logs: Array<{
          id: string;
          title: string;
          action: 'CREATE' | 'UPDATE' | 'DELETE' | 'SYNC' | 'CLEANUP';
          objectType: string;
          objectId: string;
          objectName?: string;
          changes?: Record<string, unknown>;
          userId?: string;
          userEmail?: string;
          ipAddress?: string;
          userAgent?: string;
          createdAt: string;
        }>;
        total: number;
        page: number;
        limit: number;
        totalPages: number;
      };
    }>(endpoint);

    return response.data;
  }

  async getAuditLogFilterOptions(): Promise<{
    actions: string[];
    objectTypes: string[];
    userEmails: string[];
  }> {
    const response = await this.request<{
      success: boolean;
      data: {
        actions: string[];
        objectTypes: string[];
        userEmails: string[];
      };
    }>('/api/audit/filter-options');

    return response.data;
  }

  async syncFeatures(): Promise<{ success: boolean; message: string }> {
    const response = await this.request<{ success: boolean; message: string }>(
      '/api/sync-features',
      {
        method: 'POST',
      }
    );
    return response;
  }

  async getFeatureStripeStatus(): Promise<{ success: boolean; data: FeatureStripeStatus }> {
    const response = await this.request<{ success: boolean; data: FeatureStripeStatus }>(
      '/api/features/status'
    );
    return response;
  }

  // Payment Terms
  async getPaymentTermPresets(): Promise<PaymentTermPreset[]> {
    const response = await this.request<{ success: boolean; data: PaymentTermPreset[] }>(
      '/api/billing/payment-terms/presets'
    );
    return response.data;
  }

  async getPaymentTermPreset(type: PaymentTermType): Promise<PaymentTermPreset> {
    const response = await this.request<{ success: boolean; data: PaymentTermPreset }>(
      `/api/billing/payment-terms/preset/${type}`
    );
    return response.data;
  }

  async getServiceStatus(): Promise<{
    success: boolean;
    data: {
      api: { status: string; time: string };
      db: { status: string; pathResolved: string; counts: { plans: number; features: number } };
      customerPortal: { status: string; url: string; code?: number };
      stripe: { status: string; sample?: string };
    };
  }> {
    return this.request('/api/status');
  }

  // Applications
  async getApplications(
    params: {
      status?: string;
      formType?: string;
      search?: string;
      dateFrom?: string;
      dateTo?: string;
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: string;
    } = {}
  ): Promise<{
    applications: Application[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }> {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.append(key, value.toString());
      }
    });
    const queryString = query.toString();
    const response = await this.request<{
      success: boolean;
      data: Application[];
      metadata: { pagination: { page: number; limit: number; total: number; totalPages: number } };
    }>(`/api/applications${queryString ? `?${queryString}` : ''}`);
    return { applications: response.data, pagination: response.metadata.pagination };
  }

  async getApplicationStats(): Promise<ApplicationQueueStats> {
    const response = await this.request<{ success: boolean; data: ApplicationQueueStats }>(
      '/api/applications/stats'
    );
    return response.data;
  }

  async getApplication(id: string): Promise<Application> {
    const response = await this.request<{ success: boolean; data: Application }>(
      `/api/applications/${id}`
    );
    return response.data;
  }

  async updateApplicationStatus(
    id: string,
    data: { status: string; adminNote?: string; rejectionReason?: string; blockEmail?: boolean }
  ): Promise<Application> {
    const response = await this.request<{ success: boolean; data: Application }>(
      `/api/applications/${id}/status`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );
    return response.data;
  }

  async addApplicationNote(id: string, note: string): Promise<Application> {
    const response = await this.request<{ success: boolean; data: Application }>(
      `/api/applications/${id}/notes`,
      {
        method: 'POST',
        body: JSON.stringify({ note }),
      }
    );
    return response.data;
  }

  async convertApplication(
    id: string,
    data: { planId: string; billingMethod?: string; adminNote?: string }
  ): Promise<{ application: Application; organisation: Organisation }> {
    const response = await this.request<{
      success: boolean;
      data: { application: Application; organisation: Organisation };
    }>(`/api/applications/${id}/convert`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  }

  // Blocklist
  async getBlockedEmails(): Promise<BlockedEmail[]> {
    const response = await this.request<{ success: boolean; data: BlockedEmail[] }>(
      '/api/applications/blocklist'
    );
    return response.data;
  }

  async addBlockedEmail(email: string, reason: string): Promise<void> {
    await this.request<{ success: boolean }>('/api/applications/blocklist', {
      method: 'POST',
      body: JSON.stringify({ email, reason }),
    });
  }

  async unblockEmail(id: string): Promise<void> {
    await this.request<{ success: boolean }>(`/api/applications/blocklist/${id}`, {
      method: 'DELETE',
    });
  }

  // Admin user management (for partner management)
  async getAdminUsers(): Promise<AdminUserPublic[]> {
    const response = await this.request<{ success: boolean; data: AdminUserPublic[] }>(
      '/api/admin/users'
    );
    return response.data;
  }

  async createAdminUser(data: {
    email: string;
    password: string;
    name: string;
    role: 'site_admin' | 'org_admin' | 'partner';
    orgAssignments?: Array<{ orgId: string; orgRole: 'org_full_admin' | 'org_viewer' }>;
  }): Promise<AdminUserPublic> {
    const response = await this.request<{ success: boolean; data: AdminUserPublic }>(
      '/api/admin/users',
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
    return response.data;
  }

  async updateAdminUser(
    id: string,
    data: {
      name?: string;
      isActive?: boolean;
      orgAssignments?: Array<{ orgId: string; orgRole: 'org_full_admin' | 'org_viewer' }>;
    }
  ): Promise<AdminUserPublic> {
    const response = await this.request<{ success: boolean; data: AdminUserPublic }>(
      `/api/admin/users/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );
    return response.data;
  }

  async deleteAdminUser(id: string): Promise<void> {
    await this.request<{ success: boolean }>(`/api/admin/users/${id}`, {
      method: 'DELETE',
    });
  }

  // Admin invitation management
  async inviteAdminUser(data: {
    email: string;
    name: string;
    role: 'site_admin' | 'org_admin' | 'partner';
    orgAssignments?: Array<{ orgId: string; orgRole: 'org_full_admin' | 'org_viewer' }>;
  }): Promise<AdminInvitationPublic> {
    const response = await this.request<{ success: boolean; data: AdminInvitationPublic }>(
      '/api/admin/users/invite',
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
    return response.data;
  }

  async getAdminInvitations(): Promise<AdminInvitationPublic[]> {
    const response = await this.request<{ success: boolean; data: AdminInvitationPublic[] }>(
      '/api/admin/users/invitations'
    );
    return response.data;
  }

  async resendAdminInvitation(invitationId: string): Promise<AdminInvitationPublic> {
    const response = await this.request<{ success: boolean; data: AdminInvitationPublic }>(
      `/api/admin/users/invitations/${invitationId}/resend`,
      { method: 'POST' }
    );
    return response.data;
  }

  async cancelAdminInvitation(invitationId: string): Promise<void> {
    await this.request<{ success: boolean }>(`/api/admin/users/invitations/${invitationId}`, {
      method: 'DELETE',
    });
  }

  // Public auth endpoints (no cookie needed)
  async acceptInvitation(token: string, password: string): Promise<AdminUserPublic> {
    const response = await this.request<{ success: boolean; data: AdminUserPublic }>(
      '/api/admin/auth/accept-invitation',
      {
        method: 'POST',
        body: JSON.stringify({ token, password }),
      }
    );
    return response.data;
  }

  async forgotPassword(email: string): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>('/api/admin/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(
    token: string,
    password: string
  ): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>('/api/admin/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  }

  async changePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>('/api/admin/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  // ──────────────────────────────────────────────────────────────────────
  // Platform endpoints (Task 32b — admin-only, gated by requireAuth).
  // Proxy endpoints can return 503 SERVER_UNAVAILABLE when the production
  // server is unreachable; requestPlatformProxy resolves to `null` in that
  // case so callers can render a graceful empty/error state.
  // ──────────────────────────────────────────────────────────────────────

  private async requestPlatformProxy<T>(endpoint: string): Promise<T | null> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    if (response.status === 401) {
      window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
      throw new Error('Session expired');
    }
    if (response.status === 503) {
      return null;
    }
    if (!response.ok) {
      throw new Error(`Platform request failed: ${response.status} ${response.statusText}`);
    }
    const body = await response.json();
    if (body && typeof body === 'object' && 'data' in body) {
      return body.data as T;
    }
    return body as T;
  }

  async getPlatformOrgUsers(orgId: string): Promise<ServerUser[] | null> {
    return this.requestPlatformProxy<ServerUser[]>(
      `/api/platform/orgs/${encodeURIComponent(orgId)}/users`
    );
  }

  async getPlatformOrgProjects(orgId: string): Promise<ServerProject[] | null> {
    return this.requestPlatformProxy<ServerProject[]>(
      `/api/platform/orgs/${encodeURIComponent(orgId)}/projects`
    );
  }

  async getPlatformOrgServers(orgId: string): Promise<ServerStreamingServer[] | null> {
    return this.requestPlatformProxy<ServerStreamingServer[]>(
      `/api/platform/orgs/${encodeURIComponent(orgId)}/servers`
    );
  }

  /**
   * Platform-wide aggregate served from the snapshots table (32b).
   * Cached; doesn't proxy to upstream — won't 503 on prod-server outage.
   */
  async getPlatformOverview(): Promise<{
    totalOrgs: number;
    totalUsers: number;
    totalProjects: number;
    totalProgramsOnline: number;
    snapshotAt: string | null;
  }> {
    const response = await this.request<{
      success: boolean;
      data: {
        totalOrgs: number;
        totalUsers: number;
        totalProjects: number;
        totalProgramsOnline: number;
        snapshotAt: string | null;
      };
    }>('/api/platform/overview');
    return response.data;
  }
}

export interface AdminUserPublic {
  id: string;
  email: string;
  name: string;
  role: 'site_admin' | 'org_admin' | 'partner';
  mfaEnabled: boolean;
  isActive: boolean;
  isSystemAdmin: boolean;
  orgAccess: Array<{ orgId: string; orgRole: 'org_full_admin' | 'org_viewer' }>;
  createdAt: string;
  updatedAt: string;
}

export interface AdminInvitationPublic {
  id: string;
  email: string;
  name: string;
  role: 'site_admin' | 'org_admin' | 'partner';
  invitedBy: string;
  status: 'pending' | 'accepted' | 'expired';
  orgAssignments: Array<{ orgId: string; orgRole: 'org_full_admin' | 'org_viewer' }>;
  expiresAt: string;
  acceptedAt: string | null;
  createdAt: string;
}

export const api = new ApiClient(API_BASE_URL);
